import { Component, OnInit, AfterViewInit, ViewChild, Input, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

// Components/Services/Directives/etc.

import { SelectionModel } from '@angular/cdk/collections';
import { ZipCodeApiService } from '../../../services/data/zipcode-api.service';
import { IZipCode } from '../../../models/zipcode.interface';
import { take, takeLast, debounce } from 'rxjs/operators';
import { Observable, of, timer } from 'rxjs';
import { IEngineer } from '@ebb-v2/ebb-integration';
import { LicensedDesignProfessionalApiService } from '../../../services/data/licensed-design-professional-api.service';
import { UtilityService } from '../../../services/utility/utility.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { cloneDeep } from 'lodash';

// Interfaces

@Component({
    selector: 'ebb-licensed-design-professional-search',
    templateUrl: './licensed-design-professional-search.component.html',
    styleUrls: ['./licensed-design-professional-search.component.scss'],
    standalone: false
})
export class LicensedDesignProfessionalSearchComponent implements OnInit, AfterViewInit {
  public pageTitle = 'Licensed Design Professional Directory';

  public listLicensedDesignProfessional: IEngineer[] = [];
  public listLicensedDesignProfessionalFiltered: IEngineer[] = [];
  public pagedList: IEngineer[] = [];
  public listZip: IZipCode[] = [];

  public dataSourceTable = new MatTableDataSource<IEngineer>([]);
  public selection = new SelectionModel<IEngineer>(true, []);
  public dataResultsLength = 0;
  public displayedColumns: string[] = ['bizId', 'bizContact'];
  public tablePageSizeOptions: number[] = [5, 10, 25];

  @ViewChild(MatPaginator) dataPaginator: MatPaginator;

  public waitForListingToShow = false;
  public noSearchResults = false;
  public showMore = false;

  public formSearch: FormGroup;
  public radlat1: number;
  public radlat2: number;
  public theta: number;
  public radtheta: number;
  public dist: number;
  public newSearchDistance: number;
  public oldSearchDistance: number;
  public disableSendRequest = true;       //disable send button
  public disableSelection = false;        //disable selection box on contractor
  public showCloseAfterRequest = false;
  public countSentEstimateRequests = 0;
  public appZipLoaded: string;
  public zipLat: number;
  public zipLong: number;
  public listMaxCountLimit = 5000; //initial limit to 5000 for standard searches
  public cardLayout = 100 / 3 + '%';
  public screenWidth: any;
  public isMobile: boolean;
  public specialCssClass: string;

  @Input() appZIP: string;
  @Input() dwellingLat: number;
  @Input() dwellingLong: number;
  @Input() openOption: 'default' | 'estimate' | '2ndBid';
  @Input() isPortal: string;
  @Input() genericLogo: string;

  constructor(
    private apiLdp: LicensedDesignProfessionalApiService,
    public dialog: MatDialog,
    private cd: ChangeDetectorRef,
    public readonly apiZipCode: ZipCodeApiService,
    private readonly fb: FormBuilder,
    public readonly util: UtilityService
  ) {
  }

    onResize(event) {
        this.screenWidth = event.target.innerWidth;
    }

    doCheckIsMobile(debug = false) {
      this.isMobile = window.innerWidth < 768;
      if(debug) {
        console.log(`window.innerWidth: ${window.innerWidth}
                    this.isMobile: ${this.isMobile}`);
      }
    }

  ngOnInit() {
    this.dataSourceTable.paginator = this.dataPaginator;
    this.doCheckIsMobile(false);
    this.specialCssClass = (this.isPortal)?'card-container-uneven':'card-container';
    this.initializeForms();
  }

  ngAfterViewInit() {
    this.dataSourceTable.paginator = this.dataPaginator;
  }

  private initializeForms(): void {
    this.formSearch = this.fb.group({
      searchZIP: ['', [Validators.pattern(/^[0-9]{5}$/)]],
      searchDistance: '',
      searchPhrase: '',
      requestNumber: '5',
      requestMethod: ''
    });

    this.formSearch.get('searchZIP').valueChanges
      .subscribe( selectedValue => {
        const searchDistance = this.formSearch.get('searchDistance');
        if(selectedValue){
          this.formSearch.get('searchPhrase').reset('');
          if(this.util.isEmpty(searchDistance.validator) || this.util.isEmpty(searchDistance.validator({} as AbstractControl).required)){
            searchDistance.setValidators([Validators.required]);
            searchDistance.markAsTouched();
            searchDistance.updateValueAndValidity();
          }
          this.loadData();
        } else {
          searchDistance.clearValidators();
        }
      });

    this.formSearch.get('searchDistance').valueChanges
        .subscribe( selectedValue => {
          if(selectedValue){
            this.formSearch.get('searchPhrase').reset('');
          }
          this.loadData();
      });

    this.formSearch.get('searchPhrase').valueChanges
      .pipe(debounce(() => timer(500)))
      .subscribe(selectedValue  => {
        if(selectedValue){
          this.formSearch.get('searchZIP').reset('');
          this.formSearch.get('searchDistance').reset('');
          this.loadData();
        }
      });

    this.oldSearchDistance = 5000;
    this.dataSourceTable.data = this.listLicensedDesignProfessional;
  }

  private getZipCodes() : Observable<IZipCode[]> {
    return this.apiZipCode
    .getDetailed().pipe(take(1));
  }

  private loadData(): void {
    const newSearchZip = String(this.formSearch.get('searchZIP').value).trim();
    const newSearchDistance = String(this.formSearch.get('searchDistance').value);
    const newSearchPhrase = String(this.formSearch.get('searchPhrase').value).trim();

    //search empty
    if((this.util.isEmpty(newSearchZip) || this.util.isEmpty(newSearchDistance)) && this.util.isEmpty(newSearchPhrase)){
      this.appZipLoaded = '';
      this.listLicensedDesignProfessional.length = 0;
      this.pageDirectoryData(this.listLicensedDesignProfessional);
    }
    //perform search using proximity
    else if(newSearchZip && newSearchZip.length === 5 && this.appZipLoaded !== newSearchZip && Number(newSearchZip)){
      this.formSearch.get('searchZIP').markAsTouched();
      this.waitForListingToShow = true;
      this.getZipCodes().subscribe(
        data => {
          this.listZip = data;
          const zipMatch = this.listZip.filter(zipMatch => zipMatch.name === newSearchZip);

          if(zipMatch && zipMatch[0]){
            this.zipLat = Number(zipMatch[0].latitude);
            this.zipLong = Number(zipMatch[0].longitude);
          } else {
            this.zipLat = null;
            this.zipLat = null;
          }

          this.appZipLoaded = newSearchZip;
          this.apiLdp
          .getLdpListZip(this.appZipLoaded)
          .subscribe(

            _data => {
              var data = cloneDeep(_data)
              this.listLicensedDesignProfessional.splice(0, this.listLicensedDesignProfessional.length, ...data);
              this.onSearch();
            },
            error => console.warn(error),
          );
        },
        error => {
          console.warn(`${JSON.stringify(error)}`);
        },
      )
    }
    //perform search using proximity and current data
    else if(newSearchZip && newSearchZip.length === 5 && this.appZipLoaded === newSearchZip){
      this.onSearch();
    }
    //perform name search if zip is empty
    else if(newSearchPhrase && newSearchPhrase.length > 0 && this.util.isEmpty(newSearchZip)){
      this.pageDirectoryData(this.listLicensedDesignProfessional);
      this.waitForListingToShow = true;
      this.apiLdp
      .getLdpListFilter(newSearchPhrase)
      .subscribe(
        _data => {
var data = cloneDeep(_data);
          this.listLicensedDesignProfessional.splice(0, this.listLicensedDesignProfessional.length, ...data);
          this.pageDirectoryData(this.listLicensedDesignProfessional);
        },
        error => console.warn(error),
      );
    }
  }

  public getZipErrorMessage() {
    const field = this.formSearch.get('searchZIP');

    if (field.hasError('required')) {
      return 'Enter ZIP Code';
    }

    return field.hasError('pattern') ? 'Enter valid ZIP Code' : '';
  }

  public isFormEmpty() {
    const searchZIP = this.formSearch.get('searchZIP');
    const searchDistance = this.formSearch.get('searchDistance');
    const searchPhrase = this.formSearch.get('searchPhrase');

    return (this.util.isEmpty(searchZIP.value) || this.util.isEmpty(searchDistance.value)) && this.util.isEmpty(searchPhrase.value);
  }

  public formatLink(value: string){
    if(value){
      value = value.toLowerCase();
      if(value.startsWith('http')){
        return value;
      } else {
        return `http://${value}`
      }
    }
    return '';
  }

  public onChangeCheckListToSubmit(event: any): void {
    const max = event;
    this.listMaxCountLimit = Number(max);
    this.pageDirectoryData(this.listLicensedDesignProfessionalFiltered);
  }

  private disableForm(): void{
    this.disableSendRequest = true;
    this.disableSelection = true;
    this.formSearch.disable();
  }

  public checkboxLabel(row?: IEngineer): string {
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row}`;
  }

  private pageDirectoryData(paginateList: IEngineer[]): void {
    if (Array.isArray(paginateList) && paginateList.length > 0 )
    {
      this.dataSourceTable.data = paginateList.slice(0, paginateList.length < this.listMaxCountLimit ? paginateList.length : this.listMaxCountLimit);
      this.noSearchResults = false;
      this.dataResultsLength = this.dataSourceTable.data.length;
    }
    else {
      this.dataSourceTable.data = [];
      this.noSearchResults = true;
      this.dataResultsLength = 0;
    }

    if (this.openOption === 'estimate') {
      for(let i = 0; i < this.dataSourceTable.data.length; i++) {
        if(i < this.listMaxCountLimit) {
          this.selection.select(this.dataSourceTable.data[i]);
        } else {
          this.selection.deselect(this.dataSourceTable.data[i]);
        }
      }
    }

    this.waitForListingToShow = false;
    this.cd.detectChanges();
  }

  public onCheckValidators(controlName: string): boolean {
    const retVal = (this.formSearch.get(controlName).errors
                      && (this.formSearch.get(controlName).dirty || this.formSearch.get(controlName).touched));

    const rN = this.formSearch.get('requestNumber').value;
    const rM = this.formSearch.get('requestMethod').value;
    if ((rN && rN.length > 0) && (rM && rM.length > 0))
      this.disableSendRequest = false;
    else
      this.disableSendRequest = true;

    return retVal;
  }

  public onToggleMore(event): void {
    event.preventDefault();
    this.showMore = this.showMore ? false : true;
  }

  public onSearch(): void {
    this.newSearchDistance = Number(this.formSearch.controls.searchDistance.value);
    const stringSearchDistance: string = this.formSearch.controls.searchDistance.value;
    const phrase: string = this.formSearch.get('searchPhrase').value;
    this.listLicensedDesignProfessionalFiltered = this.listLicensedDesignProfessional;

    if (stringSearchDistance !== '5000') {
      this.calculateDistances();
      this.listLicensedDesignProfessionalFiltered = this.listLicensedDesignProfessionalFiltered.filter((licensedDesignProfessional: IEngineer) =>
        (
          licensedDesignProfessional.distanceToDwelling <= this.newSearchDistance &&
            licensedDesignProfessional.distanceToDwelling !== -1
        )
      );
    }
    this.oldSearchDistance = this.newSearchDistance;
    if (phrase) {
      this.listLicensedDesignProfessionalFiltered = this.listLicensedDesignProfessionalFiltered.filter((licensedDesignProfessional: IEngineer) =>
        (
          (licensedDesignProfessional.businessName && licensedDesignProfessional.businessName.toLowerCase().startsWith(phrase.toLowerCase()) !== false) ||
          (licensedDesignProfessional.firstName && licensedDesignProfessional.firstName.toLowerCase().startsWith(phrase.toLowerCase()) !== false) ||
          (licensedDesignProfessional.lastName && licensedDesignProfessional.lastName.toLowerCase().startsWith(phrase.toLowerCase()) !== false) ||
          (licensedDesignProfessional.fullName && licensedDesignProfessional.fullName.toLowerCase().startsWith(phrase.toLowerCase()) !== false) ||
          (licensedDesignProfessional.emailAddress1 && licensedDesignProfessional.emailAddress1.toLowerCase().startsWith(phrase.toLowerCase()) !== false) ||
          (licensedDesignProfessional.ldpLicenseNumber && licensedDesignProfessional.ldpLicenseNumber.startsWith(phrase) !== false)
        )
      );
    }

    this.pageDirectoryData(this.listLicensedDesignProfessionalFiltered);
  }

  private calculateDistances() {
    const newSearchZip = String(this.formSearch.get('searchZIP').value);
    for (const i of this.listLicensedDesignProfessional) {
      i.distanceToDwelling = this.calculateDistance(parseFloat(i.address2_Latitude), parseFloat(i.address2_Longitude), newSearchZip);
    }
  }

  private calculateDistance(ldpLat: number, ldpLong: number, newSearchZip: string): number {
    //if we do not have a zip code selected then use the dwelling
    let dwellingLatLocal = this.zipLat;
    let dwellingLongLocal = this.zipLong;
    if(this.appZIP === newSearchZip && this.dwellingLat && this.dwellingLong) {
      dwellingLatLocal = this.dwellingLat;
      dwellingLongLocal = this.dwellingLong;
    }

    if ((dwellingLatLocal === ldpLat && dwellingLongLocal === ldpLong) || this.areInvalid(dwellingLatLocal, dwellingLongLocal)) {
      return -1;
    }
    else {
      this.radlat1 = Math.PI * dwellingLatLocal/180;
      this.radlat2 = Math.PI * ldpLat/180;
      this.theta = dwellingLongLocal - ldpLong;
      this.radtheta = Math.PI * this.theta/180;
      this.dist = Math.sin(this.radlat1) * Math.sin(this.radlat2) + Math.cos(this.radlat1) * Math.cos(this.radlat2) * Math.cos(this.radtheta);
      if (this.dist > 1) {
        this.dist = 1;
      }
      this.dist = Math.acos(this.dist);
      this.dist = this.dist * 180/Math.PI;
      this.dist = this.dist * 60 * 1.1515;
      return this.dist;
    }
  }

  private areInvalid (lat: number, long: number): boolean {
    if (lat == null || lat === undefined || Number.isNaN(lat) || long == null || long === undefined || Number.isNaN(long)) {
      return true;
    }
    else {
      return false;
    }
  }
}
